<template lang="pug">
ValidationProvider.vue-app-duration-input(
  ref="provider"
  v-slot="{ errors }"
  mode="lazy"
  name="appDurationInput"
  :rules="rules"
)
  .form-group(:class="{ 'has-error': errors[0] }")
    label.control-label(v-if="label") {{ inputLabel }}
    AppInput(
      ref="appInputElement"
      :value="formattedDuration"
      :disabled="disabled"
      :placeholder="placeholder"
      @up="onUp"
      @down="onDown"
      @change="onChange"
      @blur="$emit('blur')"
      @enter="onEnter"
    )
    span.help-block(v-if="showErrors && errors && errors.length > 0") {{ errors[0] }}
</template>

<script>
import { extend } from 'vee-validate';
import { formatDuration } from '~/utils/time_utils';

extend('duration', {
  validate: (value, { yearsLimit }) => {
    const parsedDuration = TimeInput.parse_duration(value);

    if (parsedDuration) {
      if (parsedDuration.years() < yearsLimit) {
        return true;
      }

      return `enter a number or decimal less than ${yearsLimit} year(s)`;
    }

    return 'enter a positive number or decimal';
  },
  params: ['yearsLimit']
});

extend('oneDay', {
  validate: (value) => {
    const parsedDuration = TimeInput.parse_duration(value);
    const oneDay = moment.duration(1, 'day');

    return parsedDuration ? oneDay - parsedDuration >= 0 : true;
  },
  message: 'Enter 24 hours or less',
});

extend('greaterThanZeroTime', {
  validate: (value) => {
    if (parseFloat(value.split(':').join('')) > 0) {
      return true;
    }

    return 'must be greater than 0';
  },
});

export default {
  name: 'AppDurationInput',
  props: {
    value: {
      required: true,
    },
    label: {
      type: String,
      default: 'Duration',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withSeconds: {
      type: [Boolean, String],
      default: 'auto',
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
    validateDay: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    greaterThanZero: {
      type: Boolean,
      default: false,
    },
    blurOnEnter: {
      type: Boolean,
      default: false,
    },
    yearsLimit: {
      type: Number,
      default: 1, // less than a year
    },
  },
  data() {
    return {
      currentValue: null,
    };
  },
  computed: {
    formattedDuration() {
      const formattedDuration = formatDuration(this.currentValue, { showSeconds: this.withSeconds });

      return formattedDuration || this.currentValue;
    },
    inputLabel() {
      let inputLabel = this.label;

      if (this.required) {
        inputLabel += '*';
      }

      return inputLabel;
    },
    rules() {
      let rules = `required|duration:${this.yearsLimit}`;

      if (this.validateDay) {
        rules += '|oneDay';
      }
      if (this.greaterThanZero) {
        rules += '|greaterThanZeroTime';
      }

      return rules;
    },
  },
  watch: {
    value(val) {
      this.currentValue = val;
    },
  },
  created() {
    this.currentValue = this.value;
  },
  methods: {
    onUp() {
      this.$emit('input', this.value.clone().add(1, 'minutes'));
    },
    onDown() {
      this.$emit('input', this.value.clone().subtract(1, 'minutes'));
    },
    onChange(val) {
      const parsedDuration = TimeInput.parse_duration(val);

      if (parsedDuration && parsedDuration.years() < this.yearsLimit) {
        this.currentValue = parsedDuration.asSeconds();
        this.$emit('input', this.currentValue);
      } else {
        this.currentValue = val;
        this.$emit('input', val);
      }
    },
    onEnter() {
      if (this.blurOnEnter) {
        this.$refs.appInputElement.$el.blur();
      }
    },
  },
};
</script>
